import { FLAGS, type FlagKey } from "./flags";
import { getFirebaseApp, isFirebaseConfigured } from "./firebase";

/**
 * Resolution order (highest priority first):
 *   1. URL query param `?flags=key:1,other:0`
 *   2. localStorage user overrides
 *   3. Remote Config (Firebase)
 *   4. Registry default
 *
 * The resolved map is cached in-memory and exposed via a subscriber list so
 * React components and the synchronous getFlag() helper see consistent values.
 */

const OVERRIDE_STORAGE_KEY = "roomcanvas:flagOverrides";

type FlagMap = Record<string, boolean | undefined>;

let urlOverrides: FlagMap = {};
let localOverrides: FlagMap = {};
let remoteValues: FlagMap = {};
let resolved: FlagMap = {};
/**
 * Stable snapshot returned by getAllFlagsSnapshot — rebuilt only when the
 * resolved map changes. `useSyncExternalStore` requires reference equality
 * across renders when nothing changed, otherwise it loops forever.
 */
let resolvedAllSnapshot: FlagMap = {};

const listeners = new Set<() => void>();

/** Notify subscribers when resolved flags change. */
function notify() {
  for (const l of listeners) l();
}

function recompute() {
  const next: FlagMap = {};
  for (const key of Object.keys(FLAGS) as FlagKey[]) {
    const def = FLAGS[key].default;
    next[key] = urlOverrides[key] ?? localOverrides[key] ?? remoteValues[key] ?? def;
  }
  let changed = false;
  for (const key of Object.keys(FLAGS) as FlagKey[]) {
    if (next[key] !== resolved[key]) {
      changed = true;
      break;
    }
  }
  resolved = next;
  if (changed) {
    resolvedAllSnapshot = { ...next };
    notify();
  }
}

function parseBoolish(v: string): boolean | null {
  const s = v.trim().toLowerCase();
  if (s === "1" || s === "true" || s === "on" || s === "yes") return true;
  if (s === "0" || s === "false" || s === "off" || s === "no") return false;
  return null;
}

function loadUrlOverrides() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("flags");
    if (!raw) return;
    const out: FlagMap = {};
    for (const part of raw.split(",")) {
      const [k, v = "1"] = part.split(":");
      const key = k?.trim() as FlagKey;
      if (!key || !(key in FLAGS)) continue;
      const parsed = parseBoolish(v);
      if (parsed != null) out[key] = parsed;
    }
    urlOverrides = out;
  } catch {
    // ignore — bad URL just means no overrides
  }
}

function loadLocalOverrides() {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(OVERRIDE_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: FlagMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (!(k in FLAGS)) continue;
      if (typeof v === "boolean") out[k as FlagKey] = v;
    }
    localOverrides = out;
  } catch {
    // corrupted JSON — start clean
  }
}

function saveLocalOverrides() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      OVERRIDE_STORAGE_KEY,
      JSON.stringify(localOverrides)
    );
  } catch {
    // storage may be unavailable — that's fine, overrides just won't persist
  }
}

/** Initialize the client. Idempotent — safe to call multiple times. */
let initialized = false;
let remotePromise: Promise<void> | null = null;
export function initFlagsClient(): Promise<void> {
  if (initialized) return remotePromise ?? Promise.resolve();
  initialized = true;
  loadUrlOverrides();
  loadLocalOverrides();
  recompute();
  remotePromise = initRemoteConfig().catch(() => {
    // swallow — graceful fallback to defaults
  });
  return remotePromise;
}

async function initRemoteConfig(): Promise<void> {
  if (!isFirebaseConfigured()) return;
  const app = getFirebaseApp();
  if (!app) return;

  // Dynamic import so we don't pay the cost when Firebase isn't configured.
  const { getRemoteConfig, fetchAndActivate, getValue } = await import(
    "firebase/remote-config"
  );

  const rc = getRemoteConfig(app);

  const defaults: Record<string, boolean> = {};
  for (const key of Object.keys(FLAGS) as FlagKey[]) {
    defaults[key] = FLAGS[key].default;
  }
  rc.defaultConfig = defaults;
  // Refresh hourly in production; instantly in dev for iteration.
  rc.settings.minimumFetchIntervalMillis = import.meta.env.DEV
    ? 0
    : 60 * 60 * 1000;

  try {
    await fetchAndActivate(rc);
  } catch {
    // network failure or RC not provisioned — fall back to defaults
    return;
  }

  const next: FlagMap = {};
  for (const key of Object.keys(FLAGS) as FlagKey[]) {
    try {
      next[key] = getValue(rc, key).asBoolean();
    } catch {
      next[key] = FLAGS[key].default;
    }
  }
  remoteValues = next;
  recompute();
}

export function subscribeFlags(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getResolvedFlags(): FlagMap {
  return resolved;
}

/**
 * Stable, reference-equal snapshot. Use this with `useSyncExternalStore`
 * (NOT {@link getResolvedFlags}, which returns a mutable internal map).
 */
export function getResolvedFlagsSnapshot(): FlagMap {
  return resolvedAllSnapshot;
}

export function setFlagOverride(key: FlagKey, value: boolean | null): void {
  if (value === null) {
    delete localOverrides[key];
  } else {
    localOverrides[key] = value;
  }
  saveLocalOverrides();
  recompute();
}

export function clearFlagOverride(key: FlagKey): void {
  setFlagOverride(key, null);
}

export function getFlagOverrides(): FlagMap {
  return { ...localOverrides };
}

/** Test-only: reset everything. */
export function _resetFlagsClientForTests(): void {
  initialized = false;
  remotePromise = null;
  urlOverrides = {};
  localOverrides = {};
  remoteValues = {};
  resolved = {};
  resolvedAllSnapshot = {};
  listeners.clear();
}
